import {useRef, useState, type ComponentProps} from 'react'
import {Banner} from '@primer/react'
import {useAnalytics} from '@github-ui/use-analytics'
import {CustomPropertiesSidebarForm} from '@github-ui/custom-properties-sidebar-form'
import {useInvalidateDefinitionsQuery} from '@github-ui/repos-picker'
import type {PropertyValue} from '@github-ui/custom-properties-types'
import {CREATE_PROPERTY_RULESET_BANNER_ANALYTICS} from './helpers'

interface Props {
  sourceName: string
  repositories: ComponentProps<typeof CustomPropertiesSidebarForm>['repositories']
  onPropertyCreated(propertyName: string, appliedValue: PropertyValue): void
}

export function NudgeBanner({repositories, sourceName, onPropertyCreated}: Props) {
  const {sendAnalyticsEvent} = useAnalytics()
  const invalidateDefinitions = useInvalidateDefinitionsQuery()
  const createPropertyButtonRef = useRef<HTMLButtonElement>(null)
  const [isDismissed, setIsDismissed] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)

  if (isDismissed) return null

  return (
    <>
      <Banner
        onDismiss={() => {
          sendAnalyticsEvent(CREATE_PROPERTY_RULESET_BANNER_ANALYTICS.dismissed)
          setIsDismissed(true)
        }}
        title="Automate targeting with custom properties"
        description={`You've manually selected ${repositories.length} repositories. A custom property lets you target them as a group and keeps the list up to date as repositories change.`}
        variant="info"
        primaryAction={
          <Banner.PrimaryAction
            type="button"
            ref={createPropertyButtonRef}
            onClick={() => {
              sendAnalyticsEvent(CREATE_PROPERTY_RULESET_BANNER_ANALYTICS.clicked)
              setIsFormOpen(true)
            }}
          >
            Create property
          </Banner.PrimaryAction>
        }
      />
      {isFormOpen && (
        <CustomPropertiesSidebarForm
          anchorRef={createPropertyButtonRef}
          onClose={() => setIsFormOpen(false)}
          repositories={repositories}
          sourceName={sourceName}
          onSuccess={(propertyName, appliedValue) => {
            onPropertyCreated(propertyName, appliedValue)
            // Invalidate the repos-picker definitions cache so the newly-created property
            // appears immediately when the user opens the filter selector.
            invalidateDefinitions()
            setIsFormOpen(false)
            sendAnalyticsEvent(CREATE_PROPERTY_RULESET_BANNER_ANALYTICS.propertyCreated)
          }}
        />
      )}
    </>
  )
}
